// src/pages/VendaMesLoja.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from "sweetalert2";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import "./vendamesloja.css";

import {
  FaChevronDown,
  FaChevronUp,
  FaUndoAlt,
  FaCalendarAlt,
  FaFilter,
  FaCheckDouble,
  FaTimes
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// ================= FORMATADORES E UTILITÁRIOS =================
const formatMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(v ?? 0);

const formatPercent = (v) =>
  isNaN(v) || v === null || v === undefined ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatLoja = (n) => String(n).padStart(2, '0');

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

// Componente de Garrafa / Progresso Meta
const MetaProgressoGarrafa = ({ percentual }) => {
  const pct = Math.min(Math.max(percentual || 0, 0), 100);
  return (
    <div className="bottle-wrapper">
      <div className="bottle-cap"></div>
      <div className="bottle-container" title={`Atingido: ${pct.toFixed(1)}%`}>
        <div className="bottle-fill" style={{ height: `${pct}%` }}></div>
      </div>
      <span className="bottle-badge">{pct.toFixed(1)}%</span>
    </div>
  );
};

// Modal Genérico Padronizado
function ModalGenerico({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1200, backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          <div className="modal-header bg-custom-red text-white py-3 px-4">
            <h5 className="modal-title font-weight-bold fs-6 m-0 text-white">{title}</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">{children}</div>
          <div className="modal-footer bg-light py-2 px-4">
            <button type="button" className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal Loading Padronizado
function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div className="spinner-border text-custom-red" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
          <span className="visually-hidden">A carregar...</span>
        </div>
        <div className="loading-text mt-3 fw-bold text-secondary">{message}</div>
      </div>
    </div>
  );
}

const VendaMesLoja = () => {
  const [showModal, setShowModal] = useState(false);
  const [showLojaModal, setShowLojaModal] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState("");
  const [dadosMes, setDadosMes] = useState([]);
  const [detalhesLoja, setDetalhesLoja] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("A carregar dados...");
  const [lojas, setLojas] = useState([]);
  const [lojasSelecionadas, setLojasSelecionadas] = useState([]);
  const [totalData, setTotalData] = useState({});

  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  // Verifica Permissões
  useEffect(() => {
    const usuario = localStorage.getItem("chapa");
    const permiteVendas = localStorage.getItem("permiteVendas");

    if (!usuario) {
      navigate("/arbor/login");
      return;
    }

    if (permiteVendas !== "S") {
      Swal.fire({
        icon: "error",
        title: "Acesso Negado",
        text: "Não é permitido acessar as vendas.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/arbor/home");
      });
    }
  }, [navigate]);

  // Define Mês Atual Padrão
  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    setMesSelecionado(`${aa}${mm}`);
  }, []);

  // Carrega Lista de Lojas Disponíveis
  useEffect(() => {
    const fetchLojas = async () => {
      try {
        const { data } = await api.get("/lojas/localizacao");
        const todasLojas = (data || []).map((loja) => formatLoja(loja.cod_emp));
        setLojas(data || []);
        setLojasSelecionadas(todasLojas);
      } catch (e) {
        console.error("Erro ao carregar lojas:", e);
        setLojas([]);
      }
    };
    fetchLojas();
  }, []);

  // Busca Vendas por Loja
  const carregarMes = useCallback(async (mesRef, lojasFiltro) => {
    const usuario = localStorage.getItem("chapa");
    if (!mesRef || !usuario) return;

    setLoading(true);
    setLoadingMessage("A carregar vendas por loja...");
    const lojasParam = (lojasFiltro || []).length > 0 ? lojasFiltro.join(",") : "";
    
    try {
      const { data } = await api.get(`venda/mes/loja/total`, {
        params: {
          mes: mesRef,
          usuario,
          ...(lojasParam ? { busca: lojasParam } : {})
        }
      });

      const dados = Array.isArray(data) ? data : [];
      setDadosMes(dados);

      if (dados.length > 0) {
        const totalVenda = dados.reduce((sum, item) => sum + (item.venda || 0), 0);
        const totalMeta = dados.reduce((sum, item) => sum + (item.meta || 0), 0);
        const somaMargemVenda = dados.reduce((sum, item) => sum + ((item.margem || 0) * (item.venda || 0)), 0);
        
        const totalAtingido = totalMeta > 0 ? (totalVenda / totalMeta) * 100 : 0;
        const totalMargem = totalVenda > 0 ? (somaMargemVenda / totalVenda) : 0;

        setTotalData({
          venda: totalVenda,
          meta: totalMeta,
          margem: totalMargem,
          atingido: totalAtingido,
        });
      } else {
        setTotalData({});
      }

      setDetalhesLoja({});
    } catch (e) {
      console.error("Erro ao carregar total das lojas:", e);
      setDadosMes([]);
      setTotalData({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Dispara busca inicial ao ter mês e lojas
  useEffect(() => {
    if (mesSelecionado && lojasSelecionadas.length > 0) {
      carregarMes(mesSelecionado, lojasSelecionadas);
    }
  }, [mesSelecionado, lojasSelecionadas, carregarMes]);

  // Detalhamento dos dias de uma Loja
  const carregarDiasLoja = async (codLoja) => {
    const usuario = localStorage.getItem("chapa");
    if (!usuario) return;

    if (detalhesLoja[codLoja]) {
      const copia = { ...detalhesLoja };
      delete copia[codLoja];
      setDetalhesLoja(copia);
      return;
    }

    setLoading(true);
    setLoadingMessage(`A carregar histórico da Loja ${formatLoja(codLoja)}...`);
    try {
      const { data } = await api.get(`venda/mes/acum`, {
        params: {
          mes: mesSelecionado,
          usuario,
          busca: codLoja
        }
      });

      const formattedData = (data || []).map((item) => ({
        ...item,
        atingido: item.meta > 0 ? (item.venda / item.meta) * 100 : 0,
      }));

      setDetalhesLoja(prev => ({ ...prev, [codLoja]: formattedData }));
    } catch (e) {
      console.error("Erro ao carregar dias da loja:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLojaToggle = (cod_emp) => {
    const codStr = formatLoja(cod_emp);
    setLojasSelecionadas(prev => 
      prev.includes(codStr) ? prev.filter(l => l !== codStr) : [...prev, codStr]
    );
  };

  const handleSelectAllLojas = () => {
    setLojasSelecionadas(lojas.map(loja => formatLoja(loja.cod_emp)));
  };

  const handleClearAllLojas = () => {
    setLojasSelecionadas([]);
  };

  // Prepara dados do gráfico geral de lojas
  const chartDataGeral = {
    labels: dadosMes.map(l => `Loja ${formatLoja(l.filial)}`),
    datasets: [
      {
        type: 'bar',
        label: '% Atingido',
        data: dadosMes.map(l => l.atingido ?? 0),
        backgroundColor: dadosMes.map(l => (l.atingido ?? 0) >= 100 ? 'rgba(16, 185, 129, 0.85)' : 'rgba(2, 132, 199, 0.85)'),
        borderColor: dadosMes.map(l => (l.atingido ?? 0) >= 100 ? 'rgba(16, 185, 129, 1)' : 'rgba(2, 132, 199, 1)'),
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: '% Margem',
        data: dadosMes.map(l => l.margem ?? 0),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        yAxisID: 'y1'
      }
    ]
  };

  const chartOptionsGeral = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { font: { size: 10, weight: '600' } }
      },
      title: {
        display: true,
        text: 'Desempenho por Loja (% Atingido vs % Margem)',
        font: { size: 13, weight: 'bold' },
        color: '#0f172a'
      },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 8,
        callbacks: {
          label: (ctx) => {
            const item = dadosMes[ctx.dataIndex];
            if (!item) return `${ctx.dataset.label}: ${ctx.raw}%`;
            if (ctx.dataset.type === 'line') {
              return `% Margem: ${formatPercent(item.margem)}`;
            }
            return [
              `Venda: ${formatMoeda(item.venda)}`,
              `Meta: ${formatMoeda(item.meta)}`,
              `% Atingido: ${formatPercent(item.atingido)}`
            ];
          }
        }
      },
      datalabels: {
        color: (ctx) => (ctx.dataset.type === 'line' ? '#d97706' : '#0f172a'),
        font: { weight: 'bold', size: isMobile ? 7 : 8 },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: (ctx) => (ctx.dataset.type === 'line' ? 0 : -90),
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: {
        ticks: { font: { size: 8 } },
        grid: { display: false }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        max: 120,
        title: { display: true, text: 'Atingido (%)', font: { weight: '600', size: 10 } },
        ticks: { callback: (val) => val + '%', font: { size: 9 } },
        grid: { color: '#f1f5f9' }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        max: 120,
        title: { display: true, text: 'Margem (%)', font: { weight: '600', size: 10 } },
        ticks: { callback: (val) => val + '%', font: { size: 9 } },
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <div className="flash-vendas-layout-wrapper">
      <Menu modulo="ARBOR" />

      <LoadingModal show={loading} message={loadingMessage} />

      <button className="btn-floating-calendar" onClick={() => setShowModal(true)} title="Selecionar Período">
        <FaCalendarAlt />
      </button>

      <div className="container-fluid px-3 px-md-4 max-width-container">
        
        {/* CABEÇALHO COM CONTROLES */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pt-2 gap-2">
          <h5 className="fw-bold m-0 text-dark fs-6">
            Consulta de Vendas por Loja - Período: <span className="text-primary">{formatarMesExibicao(mesSelecionado)}</span>
          </h5>

          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-primary btn-sm fw-semibold" onClick={() => setShowLojaModal(true)}>
              <FaFilter className="me-1" /> Lojas ({lojasSelecionadas.length})
            </button>
            <button className="btn btn-outline-secondary btn-sm fw-semibold" onClick={() => carregarMes(mesSelecionado, lojasSelecionadas)} disabled={loading}>
              <FaUndoAlt className="me-1" /> Atualizar
            </button>
          </div>
        </div>

        {/* PAINEL DO GRÁFICO GERAL */}
        {dadosMes.length > 0 && (
          <div className="chart-container position-relative mb-4">
            <div style={{ width: "100%", height: isMobile ? 220 : 260 }}>
              <Bar options={chartOptionsGeral} data={chartDataGeral} />
            </div>
          </div>
        )}

        {/* RESUMO GERAL MOBILE */}
        {isMobile && totalData.atingido !== undefined && (
          <div className="card mb-3 border shadow-sm rounded-3 bg-white p-3">
            <div className="small text-uppercase text-muted text-center fw-bold mb-2 pb-1 border-bottom">
              Resumo Geral das Lojas
            </div>
            <div className="row text-center g-2 align-items-center">
              <div className="col-5 border-end pe-1">
                <span className="x-small text-muted d-block">Venda</span>
                <strong className="text-dark font-mono-compact d-block text-truncate">
                  {formatMoeda(totalData.venda)}
                </strong>
              </div>
              <div className="col-3 border-end px-1">
                <span className="x-small text-muted d-block">Atingido</span>
                <strong className={`fs-6 ${(totalData.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                  {formatPercent(totalData.atingido)}
                </strong>
              </div>
              <div className="col-4 ps-1">
                <span className="x-small text-muted d-block">Margem</span>
                <strong className="fs-6 text-dark">{formatPercent(totalData.margem)}</strong>
              </div>
            </div>
          </div>
        )}

        {/* VISÃO DESKTOP (Tabela) */}
        {!isMobile && (
          <div className="table-frozen-wrapper shadow-sm border rounded-3 mb-4">
            <table className="table table-striped table-hover align-middle m-0 table-compact-fixed">
              <thead className="table-header-frozen">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Loja</th>
                  <th className="text-end" style={{ width: '130px' }}>Venda</th>
                  <th className="text-end" style={{ width: '130px' }}>Meta</th>
                  <th className="text-end" style={{ width: '100px' }}>% Ating.</th>
                  <th className="text-end" style={{ width: '100px' }}>Margem</th>
                  <th className="text-center" style={{ width: '80px' }}>Progresso</th>
                </tr>
              </thead>
              <tbody>
                {/* LINHA DE TOTAL NO TOPO */}
                {totalData.atingido !== undefined && (
                  <tr className="fw-bold table-secondary border-bottom">
                    <td></td>
                    <td className="text-uppercase text-primary">TOTAL GERAL</td>
                    <td className="text-end text-primary">{formatMoeda(totalData.venda)}</td>
                    <td className="text-end">{formatMoeda(totalData.meta)}</td>
                    <td className={`text-end ${(totalData.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(totalData.atingido)}
                    </td>
                    <td className="text-end text-dark">{formatPercent(totalData.margem)}</td>
                    <td className="text-center py-1">
                      <MetaProgressoGarrafa percentual={totalData.atingido} />
                    </td>
                  </tr>
                )}

                {dadosMes.map((loja) => (
                  <React.Fragment key={loja.filial}>
                    <tr onClick={() => carregarDiasLoja(loja.filial)} style={{ cursor: "pointer" }} className="fw-semibold">
                      <td className="text-center">
                        {detalhesLoja[loja.filial] ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                      </td>
                      <td className="text-dark">Loja {formatLoja(loja.filial)}</td>
                      <td className="text-end">{formatMoeda(loja.venda)}</td>
                      <td className="text-end">{formatMoeda(loja.meta)}</td>
                      <td className={`text-end ${(loja.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(loja.atingido)}
                      </td>
                      <td className="text-end text-dark">{formatPercent(loja.margem)}</td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={loja.atingido} />
                      </td>
                    </tr>

                    {/* DETALHAMENTO DIÁRIO DA LOJA */}
                    {detalhesLoja[loja.filial] && (
                      <tr className="bg-light">
                        <td colSpan="7" className="p-3 border-bottom">
                          <div className="bg-white p-3 rounded-3 border shadow-xs">
                            <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                              Evolução Diária - Loja {formatLoja(loja.filial)}
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered align-middle text-center small m-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th>Dia</th>
                                    <th>Venda</th>
                                    <th>Meta</th>
                                    <th>% Atingido</th>
                                    <th>Margem (%)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detalhesLoja[loja.filial].map((dia, idx) => (
                                    <tr key={idx}>
                                      <td className="fw-bold">{dia.numdia}</td>
                                      <td>{formatMoeda(dia.venda)}</td>
                                      <td>{formatMoeda(dia.meta)}</td>
                                      <td className={`fw-bold ${(dia.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                                        {formatPercent(dia.atingido)}
                                      </td>
                                      <td className="text-dark">{formatPercent(dia.margem)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* VISÃO MOBILE (Cards Expansíveis) */}
        {isMobile && (
          <div className="mobile-list pb-5">
            {dadosMes.map((loja) => (
              <div key={loja.filial} className="card mb-3 border-0 shadow-sm rounded-3 overflow-hidden">
                
                {/* LOJA - CABEÇALHO */}
                <div 
                  className="card-header bg-white py-3 px-3 border-0 d-flex justify-content-between align-items-center" 
                  onClick={() => carregarDiasLoja(loja.filial)} 
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fw-bold text-dark fs-6">LOJA {formatLoja(loja.filial)}</span>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`fw-bold fs-6 ${(loja.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(loja.atingido)}
                    </span>
                    {detalhesLoja[loja.filial] ? <FaChevronUp size={14} className="text-muted"/> : <FaChevronDown size={14} className="text-muted"/>}
                  </div>
                </div>

                {/* LOJA - METRICAS UNIFORMES */}
                <div className="px-3 pb-3">
                  <div className="row g-2 text-center bg-light rounded-3 py-2 border m-0">
                    <div className="col-6 border-end border-bottom pb-1">
                      <span className="text-muted d-block x-small">Venda</span>
                      <strong className="text-dark fs-6">{formatMoeda(loja.venda)}</strong>
                    </div>
                    <div className="col-6 border-bottom pb-1">
                      <span className="text-muted d-block x-small">Meta</span>
                      <strong className="text-dark fs-6">{formatMoeda(loja.meta)}</strong>
                    </div>
                    <div className="col-6 border-end pt-1">
                      <span className="text-muted d-block x-small">Atingido</span>
                      <strong className={`fs-6 ${(loja.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(loja.atingido)}
                      </strong>
                    </div>
                    <div className="col-6 pt-1">
                      <span className="text-muted d-block x-small">Margem</span>
                      <strong className="text-dark fs-6">{formatPercent(loja.margem)}</strong>
                    </div>
                  </div>
                </div>

                {/* DETALHAMENTO DIÁRIO MOBILE */}
                {detalhesLoja[loja.filial] && (
                  <div className="card-body bg-light border-top pt-2 px-2 pb-2">
                    <div className="bg-white rounded-3 p-2 border shadow-xs">
                      <div className="fw-bold text-primary fs-6 border-bottom pb-1 mb-2">
                        Dias do Mês
                      </div>
                      {detalhesLoja[loja.filial].map((dia, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-light rounded-3 border text-center">
                          <div className="d-flex justify-content-between align-items-center fw-bold border-bottom pb-1 mb-1">
                            <span>Dia {dia.numdia}</span>
                            <span className={(dia.atingido ?? 0) >= 100 ? "text-success" : "text-custom-red"}>
                              {formatPercent(dia.atingido)}
                            </span>
                          </div>
                          <div className="row g-1 x-small text-muted">
                            <div className="col-6 border-end">Venda: <b className="text-dark">{formatMoeda(dia.venda)}</b></div>
                            <div className="col-6">Meta: <b className="text-dark">{formatMoeda(dia.meta)}</b></div>
                            <div className="col-12 border-top pt-1 mt-1">Margem: <b className="text-dark">{formatPercent(dia.margem)}</b></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL SELEÇÃO DE PERÍODO */}
      <ModalGenerico
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Selecionar Período"
      >
        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Selecione o Mês / Ano:</label>
          <input 
            type="month" 
            className="form-control rounded-3" 
            value={formatarParaInput(mesSelecionado)}
            onChange={(e) => {
              if(!e.target.value) return;
              const [a, m] = e.target.value.split("-");
              const novo = `${a.slice(2)}${m}`;
              setMesSelecionado(novo); 
              carregarMes(novo, lojasSelecionadas); 
              setShowModal(false);
            }} 
          />
        </div>
      </ModalGenerico>

      {/* MODAL SELEÇÃO DE LOJAS */}
      <ModalGenerico
        show={showLojaModal}
        onClose={() => setShowLojaModal(false)}
        title="Filtrar Lojas"
      >
        <div className="d-flex justify-content-between mb-3 gap-2">
          <button className="btn btn-sm btn-outline-primary fw-semibold" onClick={handleSelectAllLojas}>
            <FaCheckDouble className="me-1" /> Selecionar Todas
          </button>
          <button className="btn btn-sm btn-outline-secondary fw-semibold" onClick={handleClearAllLojas}>
            <FaTimes className="me-1" /> Limpar
          </button>
        </div>

        <div className="lojas-selection-list pe-1" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {lojas.map((loja) => {
            const codStr = formatLoja(loja.cod_emp);
            const isChecked = lojasSelecionadas.includes(codStr);
            return (
              <div 
                key={loja.cod_emp} 
                className={`p-2 mb-2 rounded-3 border cursor-pointer d-flex justify-content-between align-items-center ${isChecked ? 'bg-light-blue border-primary' : 'bg-white'}`}
                onClick={() => handleLojaToggle(loja.cod_emp)}
              >
                <span className="small fw-semibold text-dark">
                  Filial {codStr} - {loja.razao}
                </span>
                <input 
                  type="checkbox" 
                  className="form-check-input cursor-pointer" 
                  checked={isChecked} 
                  onChange={() => {}} 
                />
              </div>
            );
          })}
        </div>

        <div className="mt-3 pt-2 border-top text-end">
          <button className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={() => {
            setShowLojaModal(false);
            carregarMes(mesSelecionado, lojasSelecionadas);
          }}>
            Aplicar Filtro
          </button>
        </div>
      </ModalGenerico>

      <Footer className="footer navbar-fixed-bottom" />
    </div>
  );
};

export default VendaMesLoja;